import { z } from 'zod';
/**
 * Urlaub und Freistellung (KON-120, KON-121). Gezählt werden Arbeitstage Montag bis Freitag ohne die gesetzlichen Feiertage des Bundeslands. Nur die Art „Urlaub“
 * geht vom Urlaubsanspruch ab. Daten sind Zeichenketten JJJJ-MM-TT, gerechnet wird in UTC, damit keine Zeitzone einen Tag verschiebt.
 */
export const GERMAN_STATES = {
    BW: 'Baden-Württemberg',
    BY: 'Bayern',
    BE: 'Berlin',
    BB: 'Brandenburg',
    HB: 'Bremen',
    HH: 'Hamburg',
    HE: 'Hessen',
    MV: 'Mecklenburg-Vorpommern',
    NI: 'Niedersachsen',
    NW: 'Nordrhein-Westfalen',
    RP: 'Rheinland-Pfalz',
    SL: 'Saarland',
    SN: 'Sachsen',
    ST: 'Sachsen-Anhalt',
    SH: 'Schleswig-Holstein',
    TH: 'Thüringen',
};
export const GERMAN_STATE_CODES = Object.keys(GERMAN_STATES);
export const DEFAULT_STATE = 'BY';
const DAY = 86_400_000;
const toUtc = (d) => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));
const fromUtc = (ms) => new Date(ms).toISOString().slice(0, 10);
const shift = (d, days) => fromUtc(toUtc(d) + days * DAY);
/** Ostersonntag nach der gregorianischen Osterformel (Meeus/Jones/Butcher) */
export function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/**
 * Gesetzliche Feiertage eines Bundeslands in einem Jahr, mit Namen. Mariä Himmelfahrt gilt in Bayern nur in Gemeinden mit überwiegend katholischer Bevölkerung, das ist
 * die große Mehrheit, deshalb zählt der Tag hier für ganz Bayern. Fronleichnam in Teilen Sachsens und Thüringens sowie das Augsburger Friedensfest fehlen, weil sie nicht
 * landesweit gelten.
 */
export function publicHolidays(year, state) {
    const y = String(year);
    const easter = easterSunday(year);
    const list = [
        [`${y}-01-01`, 'Neujahr', 'all'],
        [`${y}-01-06`, 'Heilige Drei Könige', ['BW', 'BY', 'ST']],
        [`${y}-03-08`, 'Internationaler Frauentag', ['BE', 'MV']],
        [shift(easter, -2), 'Karfreitag', 'all'],
        [easter, 'Ostersonntag', ['BB']],
        [shift(easter, 1), 'Ostermontag', 'all'],
        [`${y}-05-01`, 'Tag der Arbeit', 'all'],
        [shift(easter, 39), 'Christi Himmelfahrt', 'all'],
        [shift(easter, 49), 'Pfingstsonntag', ['BB']],
        [shift(easter, 50), 'Pfingstmontag', 'all'],
        [shift(easter, 60), 'Fronleichnam', ['BW', 'BY', 'HE', 'NW', 'RP', 'SL']],
        [`${y}-08-15`, 'Mariä Himmelfahrt', ['BY', 'SL']],
        [`${y}-09-20`, 'Weltkindertag', ['TH']],
        [`${y}-10-03`, 'Tag der Deutschen Einheit', 'all'],
        [`${y}-10-31`, 'Reformationstag', ['BB', 'HB', 'HH', 'MV', 'NI', 'SN', 'ST', 'SH', 'TH']],
        [`${y}-11-01`, 'Allerheiligen', ['BW', 'BY', 'NW', 'RP', 'SL']],
        [repentanceDay(year), 'Buß- und Bettag', ['SN']],
        [`${y}-12-25`, '1. Weihnachtstag', 'all'],
        [`${y}-12-26`, '2. Weihnachtstag', 'all'],
    ];
    return new Map(list.filter(([, , states]) => states === 'all' || states.includes(state)).map(([d, name]) => [d, name]));
}
/** Buß- und Bettag: der Mittwoch vor dem 23. November */
function repentanceDay(year) {
    const nov22 = Date.UTC(year, 10, 22);
    const weekday = new Date(nov22).getUTCDay(); // 0 = Sonntag
    return fromUtc(nov22 - ((weekday - 3 + 7) % 7) * DAY);
}
/**
 * Arbeitstage (Mo–Fr ohne Feiertage) von `start` bis einschließlich `end`. Mit `year` zählen nur die Tage in diesem Jahr, für einen Urlaub über den Jahreswechsel.
 */
export function countWorkDays(start, end, state, year) {
    if (end < start)
        return 0;
    let from = start;
    let to = end;
    if (year !== undefined) {
        if (from < `${year}-01-01`)
            from = `${year}-01-01`;
        if (to > `${year}-12-31`)
            to = `${year}-12-31`;
        if (to < from)
            return 0;
    }
    const holidays = new Map();
    let count = 0;
    for (let ms = toUtc(from); ms <= toUtc(to); ms += DAY) {
        const weekday = new Date(ms).getUTCDay();
        if (weekday === 0 || weekday === 6)
            continue;
        const day = fromUtc(ms);
        const y = Number(day.slice(0, 4));
        if (!holidays.has(y))
            holidays.set(y, publicHolidays(y, state));
        if (!holidays.get(y).has(day))
            count++;
    }
    return count;
}
// ---------------------------------------------------------------------------
// Freistellung als Anfrage (KON-121)
// ---------------------------------------------------------------------------
/** Kennung des Anfragetyps und seiner Felder. Jeder Mandant hat ihn (Migration 0027), die Arten pflegt er selbst. */
export const LEAVE_INQUIRY_TYPE = 'freistellung';
export const LEAVE_FIELDS = { start: 'start_datum', end: 'end_datum', kind: 'freistellungart' };
export const DEFAULT_LEAVE_KINDS = ['Urlaub', 'Sonderurlaub', 'Elternzeit', 'Mutterschutz', 'Berufsschule', 'Überstundenabbau'];
/** Der Anfragetyp „Freistellung“, wie neue Mandanten ihn bekommen (Migration 0027 legt denselben für bestehende an) */
export const DEFAULT_LEAVE_INQUIRY_TYPE = {
    key: LEAVE_INQUIRY_TYPE,
    label: 'Freistellung',
    sortOrder: 100,
    fields: [
        { key: LEAVE_FIELDS.start, label: 'Start Datum', type: 'date', required: true },
        { key: LEAVE_FIELDS.end, label: 'End Datum', type: 'date', required: true },
        {
            key: LEAVE_FIELDS.kind,
            label: 'Freistellungsart',
            type: 'select',
            required: true,
            options: [
                { value: 'urlaub', label: 'Urlaub' },
                { value: 'sonderurlaub', label: 'Sonderurlaub' },
                { value: 'elternzeit', label: 'Elternzeit' },
                { value: 'mutterschutz', label: 'Mutterschutz' },
                { value: 'berufsschule', label: 'Berufsschule' },
                { value: 'ueberstundenabbau', label: 'Überstundenabbau' },
            ],
        },
    ],
};
/** Nur „Urlaub“ verbraucht Urlaubsanspruch, erkannt am Wert oder an der Beschriftung der Auswahl */
export const isVacationKind = (option) => option.value.toLowerCase() === 'urlaub' || option.label.trim().toLowerCase() === 'urlaub';
export const LEAVE_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'];
export const LEAVE_STATUS_LABELS = { pending: 'Beantragt', approved: 'Genehmigt', rejected: 'Abgelehnt', cancelled: 'Zurückgezogen' };
const isoDate = z.iso.date('Ungültiges Datum');
export const createLeaveRequestInput = z
    .object({
    start: isoDate,
    end: isoDate,
    /** Wert der Auswahl „Freistellungsart“ im Anfragetyp */
    kind: z.string().trim().min(1, 'Bitte eine Art wählen').max(60),
    note: z.string().trim().max(2000).optional().transform((v) => (v ? v : undefined)),
})
    .strict()
    .refine((v) => v.end >= v.start, { message: 'Das Ende liegt vor dem Beginn', path: ['end'] });
export const decideLeaveInput = z.object({ decision: z.enum(['approved', 'rejected']), note: z.string().trim().max(1000).optional().transform((v) => (v ? v : undefined)) }).strict();
//# sourceMappingURL=leave.js.map